import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Building2, CalendarRange, Wallet, Star, ArrowRight, Loader2,
  AlertTriangle, Clock, RefreshCw, CheckCircle2,
} from "lucide-react";
import apiClient from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { MetricCard } from "@/components/dashboard/DashboardShell";
import {
  BOOKING_STATUS_LABELS, BOOKING_STATUS_STYLES,
  REFUND_STATUS_LABELS, REFUND_STATUS_STYLES,
  formatRupiah, formatDate,
} from "@/components/booking/bookingMeta";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true); setError(false);
    apiClient.get("/admin/dashboard")
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const s = data?.stats;
  const act = data?.action_required || {};
  const rec = data?.recent || {};

  const propCards = [
    { label: "Total Property", value: s?.properties?.total, hint: "Semua status", testid: "admin-stat-prop-total" },
    { label: "Property Aktif", value: s?.properties?.active, hint: "Tayang", testid: "admin-stat-prop-active" },
    { label: "Menunggu Moderasi", value: s?.properties?.pending, hint: "Perlu ditinjau", testid: "admin-stat-prop-pending" },
    { label: "Total Booking", value: s?.bookings?.total, hint: "Semua booking", testid: "admin-stat-booking-total" },
  ];
  const bookingCards = [
    { label: "Booking Pending", value: s?.bookings?.pending, hint: "Menunggu owner", testid: "admin-stat-booking-pending" },
    { label: "Booking Approved", value: s?.bookings?.approved, hint: "Disetujui", testid: "admin-stat-booking-approved" },
    { label: "Booking Completed", value: s?.bookings?.completed, hint: "Selesai", testid: "admin-stat-booking-completed" },
    { label: "Booking Cancelled", value: s?.bookings?.cancelled, hint: "Dibatalkan", testid: "admin-stat-booking-cancelled" },
  ];
  const refundCards = [
    { label: "Refund Pending", value: s?.refunds?.pending, hint: "Menunggu approval", testid: "admin-stat-refund-pending" },
    { label: "Refund Approved", value: s?.refunds?.approved, hint: "Menunggu Duitku", testid: "admin-stat-refund-approved" },
    { label: "Refund Processed", value: s?.refunds?.processed, hint: "Dikonfirmasi Duitku", testid: "admin-stat-refund-processed" },
    { label: "Total Ulasan", value: s?.reviews?.total, hint: "Semua ulasan", testid: "admin-stat-review-total" },
  ];

  const V = (v) => (loading ? "…" : (v ?? 0));

  if (error) {
    return (
      <AdminLayout testid="admin-dashboard">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Panel Admin</h1>
        <div className="mt-6 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center" data-testid="admin-dashboard-error">
          <p className="text-sm text-destructive">Gagal memuat ringkasan dashboard.</p>
          <button onClick={load} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
            <RefreshCw size={14} /> Coba lagi
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout testid="admin-dashboard">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Panel Admin</h1>
          <p className="mt-2 text-sm text-muted-foreground">Ringkasan kondisi platform AVASA secara cepat.</p>
        </div>
        <button onClick={load} data-testid="admin-dashboard-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={14} /> Muat Ulang
        </button>
      </div>

      {/* ACTION REQUIRED */}
      <div className="mt-6" data-testid="admin-action-required">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <AlertTriangle size={17} className="text-amber-500" /> Perlu Tindakan
        </h2>
        <div className="mt-3 grid sm:grid-cols-3 gap-4">
          <ActionCard to="/admin/properties" testid="admin-action-properties"
            icon={ShieldCheck} count={V(act.properties_pending)} loading={loading}
            title="Property Menunggu Review" cta="Buka Moderasi" active={act.properties_pending > 0} />
          <ActionCard to="/admin/refunds" testid="admin-action-refunds"
            icon={Wallet} count={V(act.refunds_pending)} loading={loading}
            title="Refund Menunggu Approval" cta="Buka Refund" active={act.refunds_pending > 0} />
          <ActionCard to="/admin/bookings" testid="admin-action-bookings"
            icon={CalendarRange} count={V(act.bookings_pending)} loading={loading}
            title="Booking Pending" cta="Lihat Booking" active={act.bookings_pending > 0} />
        </div>
      </div>

      {/* STATS */}
      <StatGroup title="Property & Booking" cards={propCards} V={V} start={0} />
      <StatGroup title="Status Booking" cards={bookingCards} V={V} start={4} />
      <StatGroup title="Refund & Ulasan" cards={refundCards} V={V} start={8} />

      {loading ? (
        <div className="mt-8 grid place-items-center py-10"><Loader2 className="animate-spin text-primary" size={26} /></div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-2 gap-5" data-testid="admin-recent-activity">
          {/* Recent bookings */}
          <Panel title="Booking Terbaru" icon={CalendarRange} to="/admin/bookings" cta="Semua Booking" testid="admin-recent-bookings">
            {(rec.bookings || []).length === 0 ? <Empty text="Belum ada booking." /> :
              rec.bookings.map((b, i) => (
                <RowItem key={b.id} testid={`admin-recent-booking-${i}`}
                  title={b.property_name} sub={`${b.customer_name} · ${formatDate(b.created_at)}`}
                  right={<Badge label={BOOKING_STATUS_LABELS[b.status] || b.status} cls={BOOKING_STATUS_STYLES[b.status]} />} />
              ))}
          </Panel>

          {/* Properties pending moderation */}
          <Panel title="Property Menunggu Moderasi" icon={Building2} to="/admin/properties" cta="Moderasi" testid="admin-recent-properties">
            {(rec.properties_pending || []).length === 0 ?
              <Empty text="Tidak ada property menunggu review." icon={CheckCircle2} /> :
              rec.properties_pending.map((p, i) => (
                <RowItem key={p.id} testid={`admin-recent-property-${i}`}
                  title={p.name} sub={`${p.city_name || "-"} · diajukan ${formatDate(p.submitted_at || p.created_at)}`}
                  right={<Badge label="Pending" cls="bg-amber-100 text-amber-700 border-amber-200" />} />
              ))}
          </Panel>

          {/* Recent refunds */}
          <Panel title="Refund Request Terbaru" icon={Wallet} to="/admin/refunds" cta="Semua Refund" testid="admin-recent-refunds">
            {(rec.refunds || []).length === 0 ? <Empty text="Belum ada permintaan refund." /> :
              rec.refunds.map((r, i) => (
                <RowItem key={r.id} testid={`admin-recent-refund-${i}`}
                  title={r.property_name} sub={`${formatRupiah(r.total_refund_amount)} · ${formatDate(r.created_at)}`}
                  right={<Badge label={REFUND_STATUS_LABELS[r.status] || r.status} cls={REFUND_STATUS_STYLES[r.status]} />} />
              ))}
          </Panel>

          {/* Recent reviews */}
          <Panel title="Ulasan Terbaru" icon={Star} to="/admin/reviews" cta="Semua Ulasan" testid="admin-recent-reviews">
            {(rec.reviews || []).length === 0 ? <Empty text="Belum ada ulasan." /> :
              rec.reviews.map((rv, i) => (
                <RowItem key={rv.id} testid={`admin-recent-review-${i}`}
                  title={rv.property_name}
                  sub={`${rv.customer_name} · ${formatDate(rv.created_at)}`}
                  right={<span className="inline-flex items-center gap-1 text-sm font-semibold text-accent">
                    <Star size={13} className="fill-accent text-accent" /> {rv.overall_rating}
                  </span>} />
              ))}
          </Panel>
        </div>
      )}
    </AdminLayout>
  );
}

function StatGroup({ title, cards, V, start }) {
  return (
    <div className="mt-6">
      <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{title}</h2>
      <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <MetricCard key={c.testid} index={start + i} label={c.label} value={V(c.value)} hint={c.hint} testid={c.testid} />
        ))}
      </div>
    </div>
  );
}

function ActionCard({ to, icon: Icon, count, title, cta, active, loading, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className={`block rounded-2xl border p-5 transition-transform hover:-translate-y-0.5 ${
        active ? "border-amber-300 bg-amber-50/60" : "border-border bg-card"
      }`}>
      <div className="flex items-start justify-between gap-3">
        <span className={`grid place-items-center w-10 h-10 rounded-xl ${active ? "bg-amber-500 text-white" : "bg-secondary text-primary"}`}>
          <Icon size={18} />
        </span>
        <span className={`font-heading text-3xl font-bold ${active ? "text-amber-600" : "text-foreground"}`} data-testid={`${testid}-count`}>
          {loading ? "…" : count}
        </span>
      </div>
      <p className="mt-3 text-sm font-medium text-foreground">{title}</p>
      <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary">
        {cta} <ArrowRight size={13} />
      </span>
    </Link>
  );
}

function Panel({ title, icon: Icon, to, cta, testid, children }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-heading text-base font-semibold flex items-center gap-2">
          <Icon size={16} className="text-primary" /> {title}
        </h3>
        <Link to={to} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid={`${testid}-link`}>
          {cta} <ArrowRight size={12} />
        </Link>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function RowItem({ title, sub, right, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0" data-testid={testid}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{title}</p>
        <p className="text-xs text-muted-foreground truncate">{sub}</p>
      </div>
      <div className="shrink-0">{right}</div>
    </div>
  );
}

function Badge({ label, cls }) {
  return <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${cls || "bg-muted text-muted-foreground border-border"}`}>{label}</span>;
}

function Empty({ text, icon: Icon }) {
  return (
    <p className="py-4 text-sm text-muted-foreground text-center flex items-center justify-center gap-2">
      {Icon && <Icon size={15} className="text-emerald-500" />} {text}
    </p>
  );
}
